import { useRef, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import clsx from "clsx";
import {
  ArrowTopRightOnSquareIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  CubeIcon,
  EnvelopeIcon,
  MapPinIcon,
  PencilSquareIcon,
  PhoneIcon,
  ReceiptPercentIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { GeschaeftspartnerLogo } from "@/components/GeschaeftspartnerLogo";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/apiClient";
import { formatRelative } from "@/lib/format";
import {
  deleteGeschaeftspartnerLogo,
  uploadGeschaeftspartnerLogo,
  type Geschaeftspartner,
  type GeschaeftspartnerTyp,
} from "@/lib/kontakte";
import {
  formatAdresse,
  formatSkonto,
  formatZahlungsart,
  formatZahlungsziel,
  hatEigeneZahlungsbedingungen,
  mapsUrlForAdresse,
  type DetailTab,
} from "./geschaeftspartnerDetailUtils";

interface GeschaeftspartnerHeaderProps {
  gp: Geschaeftspartner;
  typ: GeschaeftspartnerTyp;
  onEdit: () => void;
  onDelete: () => void;
  onNavigate: (tab: DetailTab) => void;
  onLogoChange?: (gp: Geschaeftspartner) => void;
}

/**
 * Kopfbereich der Detailseite: Identität (Avatar, Name, Kennzeichen), direkte
 * Kontaktwege und die wichtigsten Kennzahlen als Schnellzugriff auf die Bereiche.
 */
export function GeschaeftspartnerHeader({
  gp,
  typ,
  onEdit,
  onDelete,
  onNavigate,
  onLogoChange,
}: GeschaeftspartnerHeaderProps) {
  const isKunde = typ === "kunde";
  const nummer = isKunde ? gp.kundennummer?.trim() : gp.lieferantennummer?.trim();
  const adresse = formatAdresse(gp);
  const mapsUrl = mapsUrlForAdresse(gp);
  const effektiv = gp.zahlungsbedingungen_effektiv;
  const email = gp.email?.trim();
  const telefon = gp.telefon?.trim();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  async function onLogoFile(file: File | undefined) {
    if (!file) return;
    setLogoBusy(true);
    setLogoError(null);
    try {
      const updated = await uploadGeschaeftspartnerLogo(gp.id, file);
      onLogoChange?.(updated);
    } catch (error) {
      setLogoError(error instanceof ApiError ? error.message : "Logo konnte nicht gespeichert werden.");
    } finally {
      setLogoBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function removeLogo() {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const updated = await deleteGeschaeftspartnerLogo(gp.id);
      onLogoChange?.(updated);
    } catch (error) {
      setLogoError(error instanceof ApiError ? error.message : "Logo konnte nicht entfernt werden.");
    } finally {
      setLogoBusy(false);
    }
  }

  return (
    <section className="overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50">
      <div className="px-5 pb-5 pt-5 md:px-6 md:pt-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex items-start justify-between sm:block">
            <div className="flex flex-col items-start gap-2">
              <button
                type="button"
                className="relative rounded-2xl shadow-ios focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoBusy}
                aria-label={gp.has_logo ? "Firmenlogo ersetzen" : "Firmenlogo hinterlegen"}
              >
                <GeschaeftspartnerLogo
                  partnerId={gp.id}
                  name={gp.firmenname}
                  hasLogo={gp.has_logo}
                  logoUpdatedAt={gp.logo_updated_at}
                  className="h-16 w-16 rounded-2xl text-xl"
                />
                {logoBusy ? (
                  <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/45 text-[10px] font-medium text-white">
                    …
                  </span>
                ) : null}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="sr-only"
                onChange={(event) => void onLogoFile(event.target.files?.[0])}
              />
              <div className="flex flex-wrap gap-1.5">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={logoBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {gp.has_logo ? "Logo ersetzen" : "Logo hinterlegen"}
                </Button>
                {gp.has_logo ? (
                  <Button type="button" variant="secondary" size="sm" disabled={logoBusy} onClick={() => void removeLogo()}>
                    Entfernen
                  </Button>
                ) : null}
              </div>
              {logoError ? <p className="max-w-[14rem] text-xs text-red-600 dark:text-red-400">{logoError}</p> : null}
            </div>
            <div className="flex gap-2 sm:hidden">
              <Button
                variant="secondary"
                size="sm"
                onClick={onEdit}
                leftIcon={<PencilSquareIcon className="h-4 w-4" aria-hidden />}
              >
                Bearbeiten
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                title={gp.loesch_hinweis || undefined}
                leftIcon={<TrashIcon className="h-4 w-4" aria-hidden />}
              >
                Löschen
              </Button>
            </div>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone="accent">{isKunde ? "Kunde" : "Lieferant"}</Badge>
              {isKunde && gp.partnerart === "privatperson" ? <Badge>Privatperson</Badge> : null}
              {nummer ? <Badge mono>Nr. {nummer}</Badge> : null}
              {isKunde && gp.kundencode != null ? <Badge mono>Code {gp.kundencode}</Badge> : null}
              {gp.hero_contact_id ? (
                <Badge mono muted>
                  Hero #{gp.hero_contact_id}
                </Badge>
              ) : null}
            </div>
            <h2 className="mt-2 break-words text-2xl font-semibold tracking-tight text-label md:text-[1.75rem] md:leading-tight">
              {gp.firmenname}
            </h2>

            <div className="mt-3 flex flex-wrap gap-2">
              {email ? (
                <ContactChip href={`mailto:${email}`} icon={EnvelopeIcon} title="E-Mail schreiben">
                  {email}
                </ContactChip>
              ) : (
                <ContactChip icon={EnvelopeIcon} placeholder>
                  Keine E-Mail
                </ContactChip>
              )}
              {telefon ? (
                <ContactChip href={`tel:${telefon}`} icon={PhoneIcon} title="Anrufen">
                  {telefon}
                </ContactChip>
              ) : (
                <ContactChip icon={PhoneIcon} placeholder>
                  Kein Telefon
                </ContactChip>
              )}
              {adresse !== "—" ? (
                <ContactChip
                  href={mapsUrl ?? undefined}
                  external
                  icon={MapPinIcon}
                  title={mapsUrl ? "In Karte öffnen" : undefined}
                >
                  {adresse}
                </ContactChip>
              ) : (
                <ContactChip icon={MapPinIcon} placeholder>
                  Keine Adresse
                </ContactChip>
              )}
            </div>
          </div>

          <div className="hidden shrink-0 gap-2 sm:flex sm:pt-1">
            <Button
              variant="secondary"
              onClick={onEdit}
              leftIcon={<PencilSquareIcon className="h-4 w-4" aria-hidden />}
            >
              Bearbeiten
            </Button>
            <Button
              variant="danger"
              onClick={onDelete}
              title={gp.loesch_hinweis || undefined}
              leftIcon={<TrashIcon className="h-4 w-4" aria-hidden />}
            >
              Löschen
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 border-t border-separator/70 lg:grid-cols-4">
        <Fact
          icon={UsersIcon}
          label="Ansprechpartner"
          value={(gp.ansprechpartner_count ?? 0).toLocaleString("de-DE")}
          onClick={() => onNavigate("kontakte")}
        />
        {isKunde ? (
          <>
            <Fact
              icon={BanknotesIcon}
              label="Zahlungsart"
              value={formatZahlungsart(effektiv?.zahlungsart)}
              hint={hatEigeneZahlungsbedingungen(gp) ? "individuell" : "Firmen-Standard"}
              onClick={() => onNavigate("zahlung")}
            />
            <Fact
              icon={CalendarDaysIcon}
              label="Zahlungsziel"
              value={formatZahlungsziel(effektiv)}
              onClick={() => onNavigate("zahlung")}
            />
            <Fact
              icon={ReceiptPercentIcon}
              label="Skonto"
              value={formatSkonto(effektiv)}
              onClick={() => onNavigate("zahlung")}
            />
          </>
        ) : (
          <>
            <Fact
              icon={CubeIcon}
              label="Stamm-Artikel"
              value={(gp.artikel_count ?? 0).toLocaleString("de-DE")}
              onClick={() => onNavigate("einkauf")}
            />
            <Fact icon={MapPinIcon} label="Ort" value={gp.ort?.trim() || "—"} />
            <Fact
              icon={CalendarDaysIcon}
              label="Hero-Sync"
              value={gp.hero_synced_at ? formatRelative(gp.hero_synced_at) || "—" : "—"}
              hint={gp.hero_synced_at ? undefined : "nicht synchronisiert"}
            />
          </>
        )}
      </div>
    </section>
  );
}

function Badge({
  children,
  tone,
  mono,
  muted,
}: {
  children: ReactNode;
  tone?: "accent";
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        mono && "tabular-nums",
        tone === "accent"
          ? "bg-accent-soft text-accent"
          : muted
            ? "bg-fill-quaternary text-label-tertiary"
            : "bg-fill-secondary text-label-secondary",
      )}
    >
      {children}
    </span>
  );
}

function ContactChip({
  icon: Icon,
  children,
  href,
  external,
  title,
  placeholder,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  children: ReactNode;
  href?: string;
  external?: boolean;
  title?: string;
  placeholder?: boolean;
}) {
  const base =
    "inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition";
  if (!href) {
    return (
      <span
        className={clsx(
          base,
          placeholder
            ? "bg-fill-quaternary text-label-tertiary"
            : "bg-fill-tertiary text-label",
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 truncate">{children}</span>
      </span>
    );
  }
  return (
    <a
      href={href}
      title={title}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className={clsx(
        base,
        "bg-fill-tertiary text-label hover:bg-accent-soft hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{children}</span>
      {external ? (
        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
      ) : null}
    </a>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
  hint,
  onClick,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  value: string;
  hint?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-fill-tertiary text-label-secondary sm:flex">
        <Icon className="h-[18px] w-[18px]" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
          {label}
        </span>
        <span className="block truncate text-[15px] font-semibold tabular-nums text-label sm:text-base">
          {value}
        </span>
        {hint ? <span className="block truncate text-xs text-label-tertiary">{hint}</span> : null}
      </span>
      {onClick ? (
        <ChevronRightIcon
          className="hidden h-4 w-4 shrink-0 text-label-quaternary transition group-hover:text-label-secondary sm:block"
          aria-hidden
        />
      ) : null}
    </>
  );

  const className =
    "group flex items-center gap-3 px-4 py-3.5 text-left border-separator/70 [&:nth-child(2n)]:border-l lg:[&:nth-child(2n)]:border-l-0 lg:[&:not(:first-child)]:border-l [&:nth-child(n+3)]:border-t lg:[&:nth-child(n+3)]:border-t-0";

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={clsx(className, "transition hover:bg-fill-quaternary focus:outline-none focus-visible:bg-fill-quaternary")}
      >
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}
